import axios, { AxiosError } from "axios";
import { useCallback, useContext, useMemo } from "react";
import useSWR from "swr";
import { NotificationTypeResponse } from "../../../codegen-api";
import { AuthContext } from "../../../contexts/AuthContext";
import { API_URL } from "../../../utils/env";

export interface NotificationPreferences {
  muted_types: NotificationTypeResponse[];
  sound_enabled: boolean;
  browser_push_enabled: boolean;
  updated_at: number;
}

const DEFAULTS: NotificationPreferences = {
  muted_types: [],
  sound_enabled: true,
  browser_push_enabled: false,
  updated_at: 0,
};

const PREFERENCES_ENDPOINT = "/notification-preferences";

export function useNotificationPreferences() {
  const { apiKey, apiSecret } = useContext(AuthContext);

  const headers = useMemo(
    () =>
      apiKey && apiSecret
        ? { "API-KEY": apiKey, "API-SECRET": apiSecret }
        : undefined,
    [apiKey, apiSecret]
  );

  const swr = useSWR<NotificationPreferences, AxiosError>(
    apiKey ? [PREFERENCES_ENDPOINT, apiKey] : null,
    async () => {
      try {
        const res = await axios.get<NotificationPreferences>(
          `${API_URL}${PREFERENCES_ENDPOINT}`,
          { headers }
        );
        return { ...DEFAULTS, ...res.data };
      } catch (error) {
        return { ...DEFAULTS };
      }
    },
    { revalidateOnFocus: false }
  );

  const update = useCallback(
    async (patch: Partial<NotificationPreferences>) => {
      const optimistic: NotificationPreferences = {
        ...(swr.data ?? DEFAULTS),
        ...patch,
        updated_at: Date.now(),
      };
      swr.mutate(optimistic, { revalidate: false });

      try {
        const res = await axios.put<NotificationPreferences>(
          `${API_URL}${PREFERENCES_ENDPOINT}`,
          patch,
          { headers }
        );
        swr.mutate(res.data, { revalidate: false });
        return res.data;
      } catch (error) {
        // Roll back if server failed
        swr.mutate(undefined, { revalidate: true });
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers, swr.data, swr.mutate]
  );

  const toggleMute = useCallback(
    async (type: NotificationTypeResponse) => {
      const current = swr.data ?? DEFAULTS;
      const isMuted = current.muted_types.includes(type);
      const muted_types = isMuted
        ? current.muted_types.filter((t) => t !== type)
        : [...current.muted_types, type];
      await update({ muted_types });
    },
    [swr.data, update]
  );

  return {
    preferences: swr.data ?? DEFAULTS,
    isLoading: !swr.data && !swr.error,
    update,
    toggleMute,
    refresh: swr.mutate,
  };
}
